import React, { useState, useEffect, useContext } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Loader2,
  Play,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  RotateCcw,
  ArrowLeft,
} from "lucide-react";
import {
  sourcesApi,
  ProcessAiRequest,
  AiStatusResponse,
  AISummaryData,
  AIEvents,
  Source,
} from "@/api/source";
import { useParams, useNavigate, Link } from "react-router-dom";
import { AuthContext, AuthContextType } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import FakeLogStream from "./FakeLogStream";
import { AITimeline } from "./AIEventTimeline";

const AIGeneratedSourceEvents: React.FC<{}> = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { values } = useContext<AuthContextType>(AuthContext);
  const { user } = values;
  const [aiStatus, setAiStatus] = useState<AiStatusResponse | null>(null);
  const [sourceDetails, setSourceDetails] = useState<Source | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [processAsNewFile, setProcessAsNewFile] = useState(false);

  // Fetch source details
  const fetchSourceDetails = async () => {
    try {
      const response = await sourcesApi.read({
        id: id!,
        token: user?.access,
      });
      setSourceDetails(response.data);
    } catch (err) {
      console.error("Failed to fetch source details:", err);
      // Don't set error here as it's not critical for the main functionality
    }
  };

  // Fetch AI status
  const fetchAIStatus = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await sourcesApi.getAiStatus({
        sourceId: Number(id),
        token: user?.access,
      });
      setAiStatus(response.data);
    } catch (err) {
      console.error("Failed to fetch AI status:", err);
      setError(t("ai_generated_events.statictext.failed_to_fetch_ai_status"));
    } finally {
      setIsLoading(false);
    }
  };

  // Trigger AI processing
  const triggerAIProcessing = async (isNewFile: boolean = false) => {
    try {
      setIsProcessing(true);
      setError(null);

      const requestData: ProcessAiRequest = { is_new_file: isNewFile };
      const response = await sourcesApi.processAi({
        sourceId: Number(id),
        data: requestData,
        token: user?.access,
      });

      console.log(
        t("ai_generated_events.statictext.ai_processing_started"),
        response.data,
      );

      // Start auto-refresh to monitor progress
      setAutoRefresh(true);

      // Fetch status immediately to show processing state
      await fetchAIStatus();
    } catch (err) {
      console.error("Failed to start AI processing:", err);
      setError(
        t("ai_generated_events.statictext.failed_to_start_ai_processing"),
      );
    } finally {
      setIsProcessing(false);
    }
  };

  // Reset AI status
  const resetAIStatus = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await sourcesApi.resetAiStatus({
        sourceId: Number(id),
        token: user?.access,
      });

      console.log("AI status reset successfully:", response.data);

      // Update local state to reflect the reset
      setAiStatus((prevValue) => ({
        ai_summary_data: prevValue?.ai_summary_data || null,
        source_id: response.data.source_id,
        ai_summary_status: response.data.ai_summary_status,
        ai_summary_error_message: null,
        ai_summary_error_traceback: null,
      }));

      // Stop auto-refresh if it was running
      setAutoRefresh(false);
      setIsProcessing(false);
      setProcessAsNewFile(false);
    } catch (err) {
      console.error("Failed to reset AI status:", err);
      setError(t("ai_generated_events.statictext.failed_to_reset_ai_status"));
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-refresh when processing
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(async () => {
      await fetchAIStatus();

      // Stop auto-refresh if processing is complete or failed
      if (
        aiStatus?.ai_summary_status === "in_progress" ||
        aiStatus?.ai_summary_status === "in_queue"
      ) {
        setIsProcessing(true);
      }
      if (
        aiStatus?.ai_summary_status === "completed" ||
        aiStatus?.ai_summary_status === "failed"
      ) {
        setAutoRefresh(false);
        setIsProcessing(false);
        setProcessAsNewFile(false);
      }
    }, 5000); // Check every 5 seconds

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, aiStatus?.ai_summary_status]);

  // Initial fetch
  useEffect(() => {
    fetchSourceDetails();
    fetchAIStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Get status badge
  const getStatusBadge = (status: string | null) => {
    if (!status)
      return (
        <Badge variant="secondary">
          {t("ai_generated_events.statictext.not_started")}
        </Badge>
      );

    switch (status) {
      case "in_progress":
        return (
          <Badge variant="default" className="bg-blue-500">
            <Clock className="w-3 h-3 mr-1" />
            {t("ai_generated_events.statictext.in_progress")}
          </Badge>
        );
      case "in_queue":
        return (
          <Badge variant="default" className="bg-blue-500">
            <Clock className="w-3 h-3 mr-1" />
            {t("ai_generated_events.statictext.in_progress")}
          </Badge>
        );
      case "completed":
        return (
          <Badge variant="default" className="bg-green-500">
            <CheckCircle className="w-3 h-3 mr-1" />
            {t("ai_generated_events.statictext.completed")}
          </Badge>
        );
      case "failed":
        return (
          <Badge variant="destructive">
            <XCircle className="w-3 h-3 mr-1" />
            {t("ai_generated_events.statictext.failed")}
          </Badge>
        );
      case "partially_completed":
        return (
          <Badge variant="default" className="bg-yellow-500">
            <AlertCircle className="w-3 h-3 mr-1" />
            {t("ai_generated_events.statictext.partially_completed")}
          </Badge>
        );
      case "in_review":
        return (
          <Badge variant="default" className="bg-purple-500">
            <AlertCircle className="w-3 h-3 mr-1" />
            {t("ai_generated_events.statictext.in_review")}
          </Badge>
        );
      case "not_started":
        return (
          <Badge variant="secondary">
            {t("ai_generated_events.statictext.not_started")}
          </Badge>
        );
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  // Extract events from AI data
  const extractEvents = (data: AISummaryData | string | null): AIEvents[] => {
    if (!data) return [];

    if (typeof data === "string") {
      try {
        const parsed = JSON.parse(data);
        return extractEvents(parsed);
      } catch {
        return [];
      }
    }

    if (typeof data === "object") {
      // Check for result array
      if (data.result && Array.isArray(data.result)) {
        return data.result;
      }

      // Check for cleaned_events first
      if (data.cleaned_events && Array.isArray(data.cleaned_events)) {
        return data.cleaned_events;
      }

      // If it's an array directly
      if (Array.isArray(data)) {
        return data;
      }
    }

    return [];
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center gap-4 mb-4">
          <Button
            onClick={() => navigate(-1)}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("common.button.back")}
          </Button>
        </div>

        <CardTitle className="flex items-center justify-between">
          <span>{t("ai_generated_events.statictext.ai_generated_events")}</span>
          <div className="flex items-center gap-2">
            {aiStatus?.ai_summary_status
              ? getStatusBadge(aiStatus?.ai_summary_status)
              : "N/A"}
          </div>
        </CardTitle>

        <div className="flex flex-col md:flex-row gap-2 md:w-[80%] pt-4">
          <div className="w-full flex flex-wrap">
            <div className="w-full md:w-[50%]">
              <p>
                <b>{t("common.label.case")}: </b>
                {sourceDetails?.case ? (
                  <Link
                    className="text-primary underline"
                    to={`/cases/${sourceDetails?.case}`}
                  >
                    {t("search.statictext.view_case")}
                    {" #"}
                    {sourceDetails?.case}
                  </Link>
                ) : (
                  "N/A"
                )}
              </p>
              <p>
                <b>{t("ai_generated_events.label.source_id")} </b>
                {sourceDetails?.id}
              </p>
              <p>
                <b>{t("sources.label.institution")}: </b>
                {sourceDetails?.institution || "N/A"}
              </p>
              <p>
                <b>{t("common.label.description")}: </b>
                {sourceDetails?.description || "N/A"}
              </p>
              <p>
                <b>{t("common.label.type")}: </b>
                {sourceDetails?.type || "N/A"}
              </p>
            </div>
            <div className="w-full md:w-[50%]">
              <p>
                <b>{t("common.label.title")}: </b>
                {sourceDetails?.title || "N/A"}
              </p>
              <p>
                <b>{t("sources.label.start_date")}: </b>
                {sourceDetails?.start_date || "N/A"}
              </p>
              <p>
                <b>{t("sources.label.end_date")}: </b>
                {sourceDetails?.end_date || "N/A"}
              </p>
              <p>
                <b>{t("sources.label.total_pages")}: </b>
                {sourceDetails?.total_pages || "N/A"}
              </p>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {aiStatus?.ai_summary_status === "failed" && (
          <>
            {/* Error Display */}
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* AI Error Display */}
            {aiStatus?.ai_summary_error_message && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <div className="font-semibold mb-2">
                    {t("ai_generated_events.label.ai_processing_error")}
                  </div>
                  <div className="text-sm mb-2">
                    {aiStatus.ai_summary_error_message}
                  </div>
                  {aiStatus.ai_summary_error_traceback && (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-sm font-medium">
                        {t("ai_generated_events.label.view_error_details")}
                      </summary>
                      <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-auto max-h-40">
                        {aiStatus.ai_summary_error_traceback}
                      </pre>
                    </details>
                  )}
                </AlertDescription>
              </Alert>
            )}
          </>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button
            onClick={() => {
              setProcessAsNewFile(true);
              triggerAIProcessing(true);
            }}
            disabled={
              isProcessing ||
              isLoading ||
              aiStatus?.ai_summary_status === "in_progress" ||
              aiStatus?.ai_summary_status === "in_queue"
            }
            variant="default"
            className="flex items-center gap-2"
          >
            {isProcessing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Play className="w-4 h-4" />
            )}
            {isProcessing
              ? t("ai_generated_events.button.processing")
              : t("ai_generated_events.button.process_as_new_file")}
          </Button>

          <Button
            onClick={() => triggerAIProcessing(false)}
            disabled={
              isProcessing ||
              isLoading ||
              aiStatus?.ai_summary_status === "in_progress" ||
              aiStatus?.ai_summary_status === "in_queue"
            }
            className="flex items-center gap-2"
            variant="outline"
          >
            {isProcessing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Play className="w-4 h-4" />
            )}
            {isProcessing
              ? t("ai_generated_events.button.processing")
              : t("ai_generated_events.button.process_with_ai")}
          </Button>

          <Button
            onClick={fetchAIStatus}
            disabled={isLoading}
            variant="outline"
            className="flex items-center gap-2"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            {t("ai_generated_events.button.refresh")}
          </Button>

          {/* Reset button - only show when there's an AI status that's not "not_started" */}
          {aiStatus?.ai_summary_status &&
            aiStatus.ai_summary_status !== "not_started" && (
              <Button
                onClick={resetAIStatus}
                disabled={isProcessing || isLoading}
                variant="destructive"
                className="flex items-center gap-2"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RotateCcw className="w-4 h-4" />
                )}
                {t("ai_generated_events.button.reset_ai_status")}
              </Button>
            )}
        </div>

        {/* Auto-refresh Toggle */}
        <div className="flex items-center space-x-2">
          <Switch
            id="auto-refresh"
            checked={autoRefresh}
            onCheckedChange={setAutoRefresh}
            disabled={isProcessing || isLoading}
          />
          <Label htmlFor="auto-refresh" className="text-sm">
            {t("ai_generated_events.label.auto_refresh")}
          </Label>
          {autoRefresh && (
            <Badge variant="outline" className="text-xs">
              <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
              {t("ai_generated_events.label.auto_refreshing")}
            </Badge>
          )}
        </div>

        {/* Fake Log Stream */}
        {(aiStatus?.ai_summary_status === "in_progress" ||
          aiStatus?.ai_summary_status === "in_queue" ||
          isProcessing) &&
          sourceDetails?.total_pages && (
            <>
              <Separator />
              <FakeLogStream
                totalPages={parseInt(sourceDetails.total_pages) || 0}
                isProcessing={
                  aiStatus?.ai_summary_status === "in_progress" ||
                  aiStatus?.ai_summary_status === "in_queue" ||
                  isProcessing
                }
                isInQueue={aiStatus?.ai_summary_status === "in_queue"}
                processAsNewFile={processAsNewFile}
              />
            </>
          )}

        {/* AI Generated Timeline */}
        {aiStatus?.ai_summary_status === "completed" &&
          aiStatus?.ai_summary_data && (
            <>
              <Separator />
              <AITimeline
                events={extractEvents(aiStatus.ai_summary_data)}
                sourceId={id}
                caseId={sourceDetails?.case}
                aiSummaryData={aiStatus.ai_summary_data as AISummaryData}
              />
            </>
          )}
      </CardContent>
    </Card>
  );
};

export default AIGeneratedSourceEvents;
